import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { Calendar, Clock, Users, BarChart3, Settings, Menu, X, LogOut, Building2, Timer, FileSpreadsheet, Shield, FileText, ArrowLeftRight, LayoutDashboard, BookOpen, Trash2, Library, Brain } from 'lucide-react';
import NotificationBell from './NotificationBell';

export default function Navbar({ user, userRole, userData, companyId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      window.dispatchEvent(new Event('logout'));
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: <BarChart3 size={18} />, show: true },
    { path: '/my-dashboard', label: t('nav.myDashboard', 'My Shifts'), icon: <LayoutDashboard size={18} />, show: true },
    { path: '/rotas', label: t('nav.rotas'), icon: <Calendar size={18} />, show: true },
    { path: '/availability', label: t('nav.availability'), icon: <Clock size={18} />, show: true },
    { path: '/swap-board', label: t('nav.swapBoard', 'Swap Board'), icon: <ArrowLeftRight size={18} />, show: true },
    { path: '/staff', label: t('nav.staff'), icon: <Users size={18} />, show: ['admin', 'manager'].includes(userRole) },
    { path: '/groups', label: t('nav.groups'), icon: <Building2 size={18} />, show: ['admin', 'manager'].includes(userRole) },
    { path: '/timeclock', label: t('nav.timeClock'), icon: <Timer size={18} />, show: true },
    { path: '/payroll', label: t('nav.payroll'), icon: <FileSpreadsheet size={18} />, show: ['admin', 'manager'].includes(userRole) },
    { path: '/analytics', label: t('nav.analytics'), icon: <BarChart3 size={18} />, show: ['admin', 'manager'].includes(userRole) },
    { path: '/reports', label: t('nav.reports', 'Reports'), icon: <FileText size={18} />, show: ['admin', 'manager'].includes(userRole) },
    { path: '/policies', label: t('nav.policies', 'Policies'), icon: <Library size={18} />, show: true },
    { path: '/knowledge-hub', label: t('nav.knowledgeHub', 'Knowledge Hub'), icon: <Brain size={18} />, show: true },
    { path: '/audit-log', label: t('nav.auditLog', 'Audit Log'), icon: <Shield size={18} />, show: userRole === 'admin' },
    { path: '/subscription', label: t('nav.subscription'), icon: <Settings size={18} />, show: userRole === 'admin' },
    { path: '/settings', label: t('nav.settings'), icon: <Settings size={18} />, show: userRole === 'admin' },
    { path: '/guide', label: t('nav.guide', 'Guide'), icon: <BookOpen size={18} />, show: true },
  ];

  return (
    <>
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        {/* Top row: Logo + actions */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer shrink-0" onClick={() => handleNavigate('/dashboard')}>
              <img src="/logo.png" alt="BuildMyShifts" className="w-10 h-10 object-contain" />
            </div>

            {/* Mobile: hamburger */}
            <div className="flex items-center gap-2 lg:hidden">
              <NotificationBell user={user} companyId={companyId} />
              <LanguageSwitcher />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-300 hover:bg-white hover:text-primary transition-colors"
                aria-label="Toggle menu"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Desktop: right-side actions */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <LanguageSwitcher />
              <NotificationBell user={user} companyId={companyId} />
              {userRole === 'admin' && (
                <button
                  onClick={() => handleNavigate('/delete-account')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-amber-300 hover:bg-white hover:text-amber-600 transition-colors"
                >
                  <Trash2 size={16} />
                  Delete Account
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-red-400 hover:bg-white hover:text-red-600 transition-colors"
              >
                <LogOut size={16} />
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop: second row of nav tabs */}
        <div className="hidden lg:block border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-0.5 overflow-x-auto py-2" style={{ scrollbarWidth: 'thin' }}>
              {navItems.filter(item => item.show).map(item => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap shrink-0 ${
                    isActive(item.path)
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-300 hover:bg-white hover:text-primary'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />

          {/* Drawer panel */}
          <div className="absolute top-0 right-0 h-full w-72 max-w-[80vw] bg-slate-900 border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right">
            {/* Drawer header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="text-sm font-semibold text-white">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-md text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Drawer nav items */}
            <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-1">
              {navItems.filter(item => item.show).map(item => (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/20 text-primary'
                      : 'text-gray-300 hover:bg-white hover:text-primary'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Drawer footer */}
            <div className="border-t border-white/10 p-3 space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
                <span className="truncate">{userData?.displayName || user?.email}</span>
              </div>
              {userRole === 'admin' && (
                <button
                  onClick={() => handleNavigate('/delete-account')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-amber-300 hover:bg-amber-500/10 transition-colors"
                >
                  <Trash2 size={18} />
                  Delete Account
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut size={18} />
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
